import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import useTestResults from "@src/modules/DeviceStatus/Devices/DeviceInfoView/ResultsView/hooks/useTestResults";
import { NoDataText } from "@src/modules/DeviceStatus/Devices/DeviceInfoView/shared/components";
import ResultRow from "@src/modules/shared/components/Results/ResultRow/ResultRow";
import {
  ResultDescription,
  ResultsFormWrapper,
  ResultsTable,
  ResultsTableHeader,
  ResultsTableRow,
} from "@src/modules/shared/components/Results/ResultsForm.components";
import WarningMessage from "@src/modules/shared/components/WarningMessage";
import { StudyTestResult, TestStatus } from "@src/modules/shared/types";

interface Props {
  isEdit: boolean;
  selectedStudyId: string;
  selectedTest: StudyTestResult;
}

const ResultsForm: FunctionComponent<Props> = ({
  selectedStudyId,
  selectedTest,
  isEdit,
}) => {
  const { t } = useTranslation();

  const { data: testResultsData } = useTestResults(
    selectedStudyId,
    selectedTest.id,
  );

  const hasResults = testResultsData && testResultsData.results.length > 0;

  const hasRangeColumn =
    testResultsData?.results.some((r) => r.range !== null) ?? false;
  const hasRefDiffColumns =
    testResultsData?.results.some((r) => r.referenceValue !== null) ?? false;

  const resultsRows = testResultsData?.results.map((result, index) => (
    <ResultRow
      key={index}
      result={result}
      isEdit={isEdit}
      resultIndex={index}
      hasRangeColumn={hasRangeColumn}
      hasRefDiffColumns={hasRefDiffColumns}
    />
  ));

  const isNotes = testResultsData?.resultStatus === TestStatus.Notes;

  return (
    <ResultsFormWrapper>
      {testResultsData?.description && (
        <ResultDescription>{testResultsData?.description}</ResultDescription>
      )}
      {testResultsData?.additionalDescription && (
        <WarningMessage>
          {testResultsData?.additionalDescription}
        </WarningMessage>
      )}

      {hasResults ? (
        <ResultsTable>
          <thead>
            <ResultsTableRow>
              <ResultsTableHeader>{"\u00A0"}</ResultsTableHeader>
              {!isNotes && (
                <>
                  <ResultsTableHeader>{t("Results")}</ResultsTableHeader>
                  <ResultsTableHeader>{t("Tolerance")}</ResultsTableHeader>
                </>
              )}
              {hasRangeColumn ? (
                <ResultsTableHeader>{t("Range")}</ResultsTableHeader>
              ) : (
                hasRefDiffColumns && (
                  <>
                    <ResultsTableHeader>{t("Reference")}</ResultsTableHeader>
                    <ResultsTableHeader>{t("Difference")}</ResultsTableHeader>
                  </>
                )
              )}
            </ResultsTableRow>
          </thead>
          <tbody>{resultsRows}</tbody>
        </ResultsTable>
      ) : (
        <NoDataText>{t("No data")}</NoDataText>
      )}
    </ResultsFormWrapper>
  );
};

export default ResultsForm;
